export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'pending';
export type SubscriptionPeriodicity = 'monthly' | 'yearly';

export interface Subscription {
  id?: string;
  created_at?: Date;
  updated_at?: Date;
  subscriber_id: number;
  organization_id?: string;
  cost: number;
  periodicity: SubscriptionPeriodicity;
  status?: SubscriptionStatus;
  billing_day: number;
  duration: number;
  subscribers?: {
    id: number;
    name: string;
    email?: string;
  };
}

interface ApiResponse<T = any> {
  message: string;
  data: T;
  status: number;
}

class SubscriptionService {
  private baseUrl: string;

  constructor(baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const token = document.cookie.split('sgo_access_token=')[1]?.split(';')[0];
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async getSubscriptions(): Promise<Subscription[]> {
    const response = await this.request<Subscription[]>('/subscriptions', {
      method: 'GET',
    });
    return response.data;
  }

  async getSubscription(id: string): Promise<Subscription> {
    const response = await this.request<Subscription>(`/subscriptions/${id}`, {
      method: 'GET',
    });
    return response.data;
  }

  async createSubscription(subscription: Partial<Subscription>): Promise<{
    subscription: Subscription;
    bills: any[];
  }> {
    const response = await this.request<{
      subscription: Subscription;
      bills: any[];
    }>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
    return response.data;
  }

  async updateSubscription(id: string, updates: Partial<Subscription>): Promise<Subscription> {
    const response = await this.request<Subscription>(`/subscriptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data;
  }

  async deleteSubscription(id: string): Promise<void> {
    await this.request<null>(`/subscriptions/${id}`, {
      method: 'DELETE',
    });
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
